import { toast } from 'react-toastify';
import {
  AgentDto,
  AgentRequestDto,
  useCampaignAgentUpdateControllerExecuteMutation,
  useSquadAgentUpdateControllerExecuteMutation,
  useToolCreateControllerCreateAgentToolMutation,
  useToolUpdateControllerCreateAgentToolMutation,
  useToolDeleteControllerCreateAgentToolMutation,
} from '@shared/services/apiService/apiService';
import { ResponseMsg } from '@shared/types/admin/agent';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AgentToolsFormValue } from '../model/types';
import { agentToolsRequestMapper } from '../utils/agent-tools-mapper';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { ExpandedAgentIdleState } from '../model/idleState';

interface UseEditAgentToolsProps {
  campaignId: string;
  squadId?: string | null;
  agent?: AgentDto | null;
  // Set when saving would resend a removed voice, transcriber or LLM; nothing is saved then
  blockedReason?: string | null;
  onSuccess?: () => void;
}

export const useEditAgentTools = ({
  campaignId,
  squadId,
  agent,
  blockedReason,
  onSuccess,
}: UseEditAgentToolsProps) => {
  const { t } = useTranslation();
  const [createAgentTool] = useToolCreateControllerCreateAgentToolMutation();
  const [updateAgentTool] = useToolUpdateControllerCreateAgentToolMutation();
  const [deleteAgentTool] = useToolDeleteControllerCreateAgentToolMutation();
  const [updateCampaignAgent] = useCampaignAgentUpdateControllerExecuteMutation();
  const [updateSquadAgent] = useSquadAgentUpdateControllerExecuteMutation();

  const [responseMsg, setResponseMsg] = useState<ResponseMsg>({ message: '', type: 'success' });
  const [responseLoadingFlag, setResponseLoadingFlag] = useState(false);

  const formMethods = useForm<AgentToolsFormValue>({
    mode: 'all',
    defaultValues: {
      agentId: '',
      callbackRequestTool: { enabled: false },
      voiceMailDetectionTool: { enabled: false },
      endCallTool: { enabled: false },
      sendFollowUpMessageTool: { enabled: false, messageTemplate: '' },
      transferTool: { enabled: false, phoneNumbers: [] },
      callHandoffSignalTool: { enabled: false },
      genderDetectionTool: { enabled: false },
    },
  });

  const handleResponseMessage = (message: string, type: 'success' | 'error') => {
    setResponseMsg({ message, type });
  };

  const persistAgentSettings = async (
    nextAgent: AgentDto,
    idleState: ExpandedAgentIdleState | null,
  ) => {
    const payload: AgentRequestDto = {
      llmProviderId: nextAgent.llmProviderId,
      llmModelId: nextAgent.llmModelId ?? null,
      llmCustomModelKey: nextAgent.llmCustomModelKey ?? null,
      name: nextAgent.name,
      voiceId: nextAgent.voiceId,
      transcriberId: nextAgent.transcriberId,
      profileId: nextAgent.profileId ?? null,
      languages: nextAgent.languages || [],
      idleState,
    };

    if (squadId) {
      await updateSquadAgent({
        id: squadId,
        agentId: nextAgent.id,
        squadAgentUpdateRequestDto: {
          ...payload,
          isDefault: (nextAgent as any).isDefault,
        },
      }).unwrap();
      return;
    }

    await updateCampaignAgent({
      id: campaignId,
      agentId: nextAgent.id,
      agentRequestDto: payload,
    }).unwrap();
  };

  const onSubmit = async (idleState: ExpandedAgentIdleState | null = null): Promise<boolean> => {
    let success = false;

    // Checked before any tool request so the tools and the agent are not saved halfway
    if (blockedReason) {
      handleResponseMessage(blockedReason, 'error');
      toast.error((props) => <CustomErrorToast {...props} message={blockedReason} />, {
        closeButton: false,
        autoClose: 8000,
        icon: false,
      });
      return false;
    }

    await formMethods.handleSubmit(
      async (data) => {
        if (!agent) {
          return;
        }

        setResponseLoadingFlag(true);
        setResponseMsg({ message: '', type: 'success' });

        try {
          const toolsToSave = agentToolsRequestMapper(data);
          const agentId = data.agentId;

          // Get the list of tool types that should exist (enabled tools)
          const enabledToolTypes = new Set(toolsToSave.map((t) => t.type));

          // Get all existing tool types from the form data
          const allToolTypes = [
            data.endCallTool,
            data.callbackRequestTool,
            data.voiceMailDetectionTool,
            data.sendFollowUpMessageTool,
            data.transferTool,
            data.callHandoffSignalTool,
            data.genderDetectionTool,
          ].filter(Boolean);

          const operations: Promise<any>[] = [];

          // Process each tool
          for (const tool of toolsToSave) {
            if (tool.id) {
              // Update existing tool
              operations.push(
                updateAgentTool({
                  id: agentId,
                  toolId: tool.id,
                  toolUpdateRequestDto: {
                    isAsync: tool.isAsync,
                    messageTemplate: tool.messageTemplate,
                    phoneNumbers: tool.phoneNumbers,
                    mcpName: tool.mcpName,
                    mcpServerConfig: tool.mcpServerConfig,
                    agentId: tool.agentId,
                    genderSettings: tool.genderSettings,
                  },
                }).unwrap(),
              );
            } else {
              operations.push(
                createAgentTool({
                  id: agentId,
                  toolRequestDto: tool,
                }).unwrap(),
              );
            }
          }

          for (const toolData of allToolTypes) {
            if (toolData?.id && !enabledToolTypes.has(toolData.type)) {
              operations.push(
                deleteAgentTool({
                  id: agentId,
                  toolId: toolData.id,
                }).unwrap(),
              );
            }
          }

          await Promise.all(operations);
          await persistAgentSettings(agent, idleState);

          const successMsg = t('adminMainPage.agent.toolsSavedSuccess');
          handleResponseMessage(successMsg, 'success');

          toast.success(CustomSuccessToast, {
            closeButton: false,
            autoClose: 5000,
            icon: false,
            data: {
              title: t('common.saved'),
              description: successMsg,
            },
          });

          if (onSuccess) {
            onSuccess();
          }

          success = true;
        } catch (error) {
          console.error('Error saving agent tools:', error);
          handleResponseMessage(
            getApiErrorMessage(error, t('adminMainPage.agent.toolsSaveFailed')),
            'error',
          );
        } finally {
          setResponseLoadingFlag(false);
        }
      },
      async () => {
        setResponseLoadingFlag(true);
        const data = formMethods.getValues();

        try {
          const toolsToSave = agentToolsRequestMapper(data);
          const agentId = data.agentId;

          const enabledToolTypes = new Set(toolsToSave.map((t) => t.type));

          const allToolTypes = [
            data.endCallTool,
            data.callbackRequestTool,
            data.voiceMailDetectionTool,
            data.sendFollowUpMessageTool,
            data.transferTool,
            data.callHandoffSignalTool,
            data.genderDetectionTool,
          ].filter(Boolean);

          const operations: Promise<any>[] = [];

          for (const tool of toolsToSave) {
            if (tool.id) {
              operations.push(
                updateAgentTool({
                  id: agentId,
                  toolId: tool.id,
                  toolUpdateRequestDto: {
                    isAsync: tool.isAsync,
                    messageTemplate: tool.messageTemplate,
                    phoneNumbers: tool.phoneNumbers,
                    mcpName: tool.mcpName,
                    mcpServerConfig: tool.mcpServerConfig,
                    agentId: tool.agentId,
                  },
                }).unwrap(),
              );
            } else {
              operations.push(
                createAgentTool({
                  id: agentId,
                  toolRequestDto: tool,
                }).unwrap(),
              );
            }
          }

          for (const toolData of allToolTypes) {
            if (toolData?.id && !enabledToolTypes.has(toolData.type)) {
              operations.push(
                deleteAgentTool({
                  id: agentId,
                  toolId: toolData.id,
                }).unwrap(),
              );
            }
          }

          await Promise.all(operations);
        } catch (error) {
          console.error('Error saving agent tools (invalid):', error);
          handleResponseMessage(
            getApiErrorMessage(error, t('adminMainPage.agent.toolsSaveFailed')),
            'error',
          );
        }

        setResponseLoadingFlag(false);
      },
    )();

    return success;
  };

  return {
    onSubmit,
    setResponseMsg,
    responseMsg,
    responseLoadingFlag,
    formMethods,
  };
};
